import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { format } from "date-fns"; 

// --- Replicating External Helpers & Constants ---
const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const safeFormatDate = (dateValue) => {
    if (!dateValue) return '-';
    try {
        const date = new Date(dateValue);
        if (date instanceof Date && !isNaN(date.getTime())) {
            return format(date, "dd-MM-yyyy");
        }
    } catch (e) {
        console.error("Error formatting date:", e);
    }
    return String(dateValue).split('T')[0] || '-'; // Fallback
};

// Define the exact column structure
const SALESLIST_CASH_COLUMNS = [
    { header: 'DEPT NO', key: 'DEPTNO', numeric: false },
    { header: 'SR NO', key: 'SR_NO', numeric: false }, 
    { header: 'INV NO', key: 'INVOICE_NO', numeric: false },
    { header: 'DATE', key: 'SR_DATE', isDate: true, numeric: false },
    { header: 'CUSTOMER', key: 'CUSTOMER', numeric: false },
    { header: 'LPO', key: 'LPO', numeric: false },
    { header: 'SALES ID', key: 'SALESPERSON_ID', numeric: false }, 
    { header: 'SALES MAN', key: 'SALES_PERSON', numeric: false, isLabel: true }, // LABEL CELL
    { header: 'ACCOUNT', key: 'ACCOUNT', numeric: false },
    { header: 'AMOUNT', key: 'AMOUNT', numeric: true },
    { header: 'VAT AMT', key: 'VATAMT', numeric: true },
    { header: 'NET RETURN', key: 'NET RETURN', numeric: true }, 
    { header: 'COST', key: 'COST', numeric: true },
    { header: 'TYPE', key: 'TYPE', numeric: false },
];
const TOTAL_COLUMNS = SALESLIST_CASH_COLUMNS.length; 
const TOTAL_KEYS = ['AMOUNT', 'VATAMT', 'NET RETURN', 'COST']; // Keys to total in the footer
const LABEL_KEY = SALESLIST_CASH_COLUMNS.find(col => col.isLabel).key; // 'SALES_PERSON'

// --- Border Color Constants for clarity and consistency ---
const DEFAULT_BORDER_COLOR_HEADER = '#ccc';
const DEFAULT_BORDER_COLOR_BODY = '#eee';
const DEFAULT_BORDER_COLOR_FOOTER = '#000';
// -----------------------------------------------------------

// Determine column widths (Needs to fit many columns, use landscape and adjust widths)
const totalTableWidth = 100; // Percentage
const defaultColumnWidth = (totalTableWidth / TOTAL_COLUMNS).toFixed(2); // Uniform width distribution as string

// Stylesheet definition
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 10,
        fontSize: 7, // Smaller font for many columns
    },
    table: {
        display: 'flex',
        width: 'auto', // Will expand to fit content based on column widths
        flexDirection: 'column',
        // borderWidth: 0, 
    },
    headerRow: {
        flexDirection: 'row',
        backgroundColor: '#e0f7fa',
        // borderBottomWidth: 1,
        // borderBottomColor: DEFAULT_BORDER_COLOR_HEADER,
        textAlign: 'center',
        fontWeight: 'bold',
    },
    row: {
        flexDirection: 'row',
        // borderBottomWidth: 1,
        // borderBottomColor: DEFAULT_BORDER_COLOR_BODY,
    },
    // Base cell styles - NO BORDER PROPERTIES HERE
    cell: {
        padding: 3,
        overflow: 'hidden', 
        justifyContent: 'center', // Default to center content vertically
    },
    headerCell: {
        padding: 3,
        fontWeight: 'bold',
        justifyContent: 'center', // Default to center content vertically
    },
    numeric: {
        textAlign: 'right',
    },
    left: {
        textAlign: 'left',
    },
    footerRow: {
        flexDirection: 'row',
        backgroundColor: '#b3b3b3',
        // borderTopWidth: 2, 
        // borderTopColor: DEFAULT_BORDER_COLOR_FOOTER,
        fontWeight: 'bold',
    },
    footerCell: {
        padding: 5,
        fontWeight: 'bold',
        fontSize: 9,
        justifyContent: 'center', // Default to center content vertically
    },
});

const SalesTableFlatCASHReturnPDF = ({ rows, grandColumnTotals }) => {
    
    // Helper function to calculate the span index for the GRAND TOTAL label
    const calculateDescriptiveSpanIndex = () => {
        const labelColIndex = SALESLIST_CASH_COLUMNS.findIndex(col => col.isLabel);
        if (labelColIndex === -1) return 0; // Should not happen if LABEL_KEY is defined

        let firstTotalColIndex = TOTAL_COLUMNS;
        const firstTotalKeyCol = SALESLIST_CASH_COLUMNS.find(col => TOTAL_KEYS.includes(col.key));
        if (firstTotalKeyCol) {
            firstTotalColIndex = SALESLIST_CASH_COLUMNS.findIndex(col => col.key === firstTotalKeyCol.key);
        }
        
        // The span covers all columns from the start up to the one BEFORE the first total column.
        // If the label is before the first total column, the span ends there.
        // Otherwise, it spans up to and including the label.
        return Math.max(labelColIndex + 1, firstTotalColIndex);
    };

    const descriptiveSpan = calculateDescriptiveSpanIndex();
    
    // Calculate the width of the descriptive span (as a percentage string)
    const descriptiveSpanWidth = (descriptiveSpan * defaultColumnWidth * 1).toFixed(2) + '%';


    if (rows.length === 0 && (!grandColumnTotals || Object.keys(grandColumnTotals).length === 0)) {
        return (
            <Document>
                <Page size="A4" style={styles.page} orientation="landscape">
                    <Text style={{ textAlign: 'center', marginTop: 50 }}>No sales data found for the report.</Text>
                </Page>
            </Document>
        );
    }

    const renderHeader = () => (
        <View style={styles.headerRow} fixed>
            {SALESLIST_CASH_COLUMNS.map((col, index) => {
                const isLastCell = index === TOTAL_COLUMNS - 1;
                return (
                    <View 
                        key={col.key} 
                        style={[
                            styles.headerCell, 
                            col.numeric ? styles.numeric : styles.left, 
                            { 
                                width: `${defaultColumnWidth}%`, // Ensure width is a string with %
                                // borderRightWidth: isLastCell ? 0 : 1, // Explicitly 0 or 1
                                // borderRightColor: DEFAULT_BORDER_COLOR_HEADER,
                            }
                        ]}
                    >
                        <Text>{col.header}</Text>
                    </View>
                );
            })}
        </View>
    );

    const renderBody = () => (
        rows.map((row, index) => (
            <View key={`row-cash-${index}`} style={styles.row} wrap={false}>
                {SALESLIST_CASH_COLUMNS.map((col, dataIndex) => {
                    const cellValue = row[col.key] || null;
                    const isLastCell = dataIndex === TOTAL_COLUMNS - 1;

                    let content = cellValue;
                    if (col.numeric) {
                        content = col.key === 'SR_NO' ? cellValue : formatNumber(cellValue);
                    } else if (col.isDate) {
                        content = safeFormatDate(cellValue);
                    } 
                    
                    return (
                        <View
                            key={col.key}
                            style={[
                                styles.cell,
                                col.numeric ? styles.numeric : styles.left,
                                col.key === 'AMOUNT' ? { fontWeight: 'bold' } : {}, // Apply bold style
                                { 
                                    width: `${defaultColumnWidth}%`, // Ensure width is a string with %
                                    // borderRightWidth: isLastCell ? 0 : 1, // Explicitly 0 or 1
                                    // borderRightColor: DEFAULT_BORDER_COLOR_BODY,
                                }
                            ]}
                        >
                            <Text>{content || '-'}</Text>
                        </View>
                    );
                })}
            </View>
        ))
    );

    const renderFooter = () => {
        
        return (
            <View style={styles.footerRow} fixed>
                {/* 1. Descriptive Span for the GRAND TOTAL label */}
                <View 
                    style={[
                        styles.footerCell, 
                        styles.numeric, 
                        { 
                            width: descriptiveSpanWidth, // Use pre-calculated string width
                           // borderRightWidth: 0, // Explicitly 0
                        }
                    ]}
                >
                    <Text>GRAND TOTAL:</Text>
                </View>
                
                {/* 2. Data cells starting from the column where the totals begin */}
                {/* Ensure we slice correctly based on descriptiveSpan */}
                {SALESLIST_CASH_COLUMNS.slice(descriptiveSpan).map((col, index) => {
                    const isTotalKey = TOTAL_KEYS.includes(col.key);
                    // Check last cell within this *sliced* array
                    const isLastCellInSlice = index === SALESLIST_CASH_COLUMNS.slice(descriptiveSpan).length - 1;
                    
                    let content = '';
                    if (isTotalKey) {
                        content = formatNumber(grandColumnTotals[col.key] || 0);
                    }

                    return (
                        <View 
                            key={col.key} 
                            style={[
                                styles.footerCell, 
                                styles.numeric, 
                                { 
                                    width: `${defaultColumnWidth}%`, // Ensure width is a string with %
                                    // borderRightWidth: isLastCellInSlice ? 0 : 1, // Explicitly 0 or 1
                                    // borderRightColor: DEFAULT_BORDER_COLOR_FOOTER,
                                }
                            ]}
                        >
                            <Text>{content}</Text>
                        </View>
                    );
                })}
            </View>
        );
    };

    return (
        <Document>
            {/* Set orientation to landscape to fit more columns */}
            <Page size="A4" style={styles.page} orientation="landscape">
                <View style={styles.table}>
                    {renderHeader()}
                    {renderBody()}
                    {grandColumnTotals && Object.keys(grandColumnTotals).length > 0 && renderFooter()}
                </View>
            </Page>
        </Document>
    );
};

export default SalesTableFlatCASHReturnPDF;